import {Box, Button, FormHelperText, MenuItem, Stack, TextField} from '@mui/material';
import {DatePicker} from '@mui/x-date-pickers';
import {useEffect, useState} from 'react';
import {toDateMoment, toDateOnly} from '../../../../../utility/dateUtil';

/* eslint-disable react/prop-types */
const MedicalEventPopup = ({selectedMedicalEvent, medicalEventLookups, onSubmit, onClose}) => {
    const [formData, setFormData] = useState({
        eventTypeId: selectedMedicalEvent?.eventTypeId || '',
        eventId: selectedMedicalEvent?.eventId || '',
        eventDate: toDateOnly(selectedMedicalEvent?.eventDate),
        followUpDate: toDateOnly(selectedMedicalEvent?.followUpDate),
        timeSeen: selectedMedicalEvent?.timeSeen || '',
    });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        setFormData({
            eventTypeId: selectedMedicalEvent?.eventTypeId || '',
            eventId: selectedMedicalEvent?.eventId || '',
            eventDate: toDateOnly(selectedMedicalEvent?.eventDate),
            followUpDate: toDateOnly(selectedMedicalEvent?.followUpDate),
            timeSeen: selectedMedicalEvent?.timeSeen || '',
        });
        setErrors({});
    }, [selectedMedicalEvent]);

    useEffect(() => {
        validateForm();
    }, [formData]);

    const validateForm = () => {
        const newErrors = {};
        const eventDate = toDateMoment(formData.eventDate);
        const followUpDate = toDateMoment(formData.followUpDate);

        if (eventDate && followUpDate && !eventDate.isBefore(followUpDate)) {
            newErrors.followUpDate = 'Follow Up Date should be greater than Event Date';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = () => {
        if (validateForm()) {
            onSubmit({
                ...formData,
                eventDate: toDateOnly(formData.eventDate),
                followUpDate: toDateOnly(formData.followUpDate),
            });
        }
    };

    const handleDateChange = (name, value) => {
        setFormData((prev) => ({...prev, [name]: toDateOnly(value)}));
    };

    // AD-2812: SIMS needs a date on every medical event, so a new event must have an Event Date. An existing event
    // (some came from SIMS without a date) can still be edited as before.
    const isNewEvent = !selectedMedicalEvent || Boolean(selectedMedicalEvent.isNew);
    const isFormValid = Object.keys(errors).length === 0 && formData.eventTypeId && formData.eventId &&
        (formData.eventDate || !isNewEvent);

    return (
        <Box
            sx={{
                padding: 1,
                paddingTop: 0,
                backgroundColor: 'white',
                margin: 'auto',
                marginTop: 4,
            }}
        >
            <Stack spacing={2}>
                <TextField
                    select
                    label="Event Type"
                    name="eventTypeId"
                    value={formData.eventTypeId}
                    onChange={(e) => {
                        const eventTypeLookup = medicalEventLookups.eventTypes.find(lookup => lookup.id === e.target.value);

                        setFormData((prev) => ({
                            ...prev,
                            eventTypeId: e.target.value,
                            eventId: eventTypeLookup?.medicalEvents.length === 1
                                ? eventTypeLookup?.medicalEvents[0]?.id
                                : null
                        }));
                    }}
                    fullWidth
                    error={Boolean(errors.eventTypeId)}
                    helperText={errors.eventTypeId}
                    required
                >
                    {medicalEventLookups.eventTypes.map((type) => (
                        <MenuItem key={type.id} value={type.id}>
                            {type.description}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField
                    select
                    label="Description"
                    name="eventId"
                    value={formData.eventId}
                    onChange={(e) => {
                        const eventLookup = medicalEventLookups.medicalEvents.find(lookup => lookup.id === e.target.value);
                        setFormData((prev) => ({
                            ...prev,
                            eventId: e.target.value,
                            eventTypeId: eventLookup?.relatedSimsId
                        }));
                    }}
                    fullWidth
                    error={Boolean(errors.eventId)}
                    helperText={errors.eventId}
                    required
                >
                    {formData.eventTypeId
                        ? medicalEventLookups.eventTypes.find(type => type.id === formData.eventTypeId)?.medicalEvents.map(event => (
                            <MenuItem key={event.id} value={event.id}>
                                {event.description}
                            </MenuItem>
                        ))
                        : medicalEventLookups.medicalEvents.map(event => (
                            <MenuItem key={event.id} value={event.id}>
                                {event.description}
                            </MenuItem>
                        ))}
                </TextField>
                <DatePicker
                    label="Event Date"
                    value={toDateMoment(formData.eventDate)}
                    onChange={(date) => handleDateChange('eventDate', date)}
                    renderInput={(params) => (
                        <TextField {...params} fullWidth error={Boolean(errors.eventDate)}/>
                    )}
                    slotProps={{textField: {required: isNewEvent}}}
                />
                {
                    errors.eventDate && <FormHelperText error>{errors.eventDate}</FormHelperText>
                }
                {/* AD-2717: the time the student was seen, held in Admicity only */}
                <TextField
                    label="Time Seen"
                    type="time"
                    value={formData.timeSeen}
                    onChange={(e) => setFormData((prev) => ({...prev, timeSeen: e.target.value}))}
                    InputLabelProps={{shrink: true}}
                    inputProps={{'data-testid': 'medical-event-time-seen', step: 60}}
                    fullWidth
                />
                <DatePicker
                    label="Follow Up Date"
                    value={toDateMoment(formData.followUpDate)}
                    onChange={(date) => handleDateChange('followUpDate', date)}
                    renderInput={(params) => (
                        <TextField {...params} fullWidth error={Boolean(errors.followUpDate)}/>
                    )}
                />
                {
                    errors.followUpDate && <FormHelperText error>{errors.followUpDate}</FormHelperText>
                }
            </Stack>
            <Stack direction="row" justifyContent="flex-end" spacing={2} marginTop={2}>
                <Button onClick={onClose} variant="outlined">
                    Cancel
                </Button>
                <Button onClick={handleSubmit} variant="contained" color="primary" disabled={!isFormValid}>
                    Save
                </Button>
            </Stack>
        </Box>
    );
};

export default MedicalEventPopup;
